import { Box, Typography, useTheme, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import TuneIcon from "@mui/icons-material/Tune";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListPageHeader from "components/list/ListPageHeader";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import FixedSizeDialog from "components/fixedSizeDialog";
import StockAdjustmentViewForm from "../forms/adjustmentForms/stockAdjustmentViewForm";
import { useAdjustmentsList } from "api/hooks/inventory";
import { requester } from "api/pesabackend";
import useListDateFilter from "hooks/useListDateFilter";

export default function StockAdjustments() {
  const navigate = useNavigate();
  const theme = useTheme();
  const textPri = theme.palette.neutral.dark;
  const accent = theme.palette.secondary.main;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const {
    adjustmentList, count, isLoadingAdjustmentList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange,
  } = useAdjustmentsList(1, business_branch_id);

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState({});

  const handleClickView = async (id) => {
    const res = await requester.get(`/inventory/stock_adjustment/get/${id}`);
    if (res.status === 200) { setViewData(res.data.data); setOpenView(true); }
  };

  const columns = [
    { label: "Adjustment No." },
    { label: "Date Carried Out" },
    { label: "Items Adjusted", align: "center" },
    { label: "Adjustment Value", align: "right" },
    { label: "Carried Out By" },
    { label: "Actions", align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.alt, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <ListPageHeader icon={<TuneIcon sx={{ fontSize: "22px" }} />} title="Stock Adjustments" count={count}>
        <Button onClick={() => navigate("/adjustmentForm")} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
          Add Adjustment
        </Button>
      </ListPageHeader>

      <ListFilterBar>
        <ListDateFilterBar embedded dateFilter={displayDateFilter} customRange={customRange}
          onFilterChange={handleFilterChange} onCustomRangeChange={setCustomRange} onCustomApply={handleCustomApply} />
      </ListFilterBar>

      <ReportTable
        columns={columns}
        rows={adjustmentList}
        loading={isLoadingAdjustmentList}
        hasMore={hasMore}
        onLoadMore={loadMore}
        loadingMore={isLoadingMore}
        emptyMessage="No stock adjustments found for this period"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.id}</Typography></RCell>
            <RCell>{moment(row.date_added).format("LLL")}</RCell>
            <RCell align="center">{row.items_adjusted}</RCell>
            <RCell align="right"><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.total_adjustment_value}</Typography></RCell>
            <RCell>{row.added_by.first_name} {row.added_by.last_name}</RCell>
            <RCell align="center">
              <Box onClick={() => handleClickView(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(29,158,117,0.3)", cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                <VisibilityIcon sx={{ fontSize: "14px" }} /> View Details
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <FixedSizeDialog open={openView} handleClose={() => setOpenView(false)} dialogHeading="Stock Adjustment Details" negativeResponse="Close" maxWidth="md" scroll="body"
        content={<StockAdjustmentViewForm viewData={viewData} />}
      />
    </Box>
  );
}
